import { EFeatures } from '@constants';
import { lazy } from 'react';

import { IRoute } from '../../types/routes';

import { LiveEventLottie } from '@/components/LiveEventLottie';
import { PendingChatsBadge } from '@/components/PendingChatsBadge';

const OnboardingPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.OnboardingPage }))
);
const StudentDashboardPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.DashboardPage }))
);
const CoursePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.CoursePage }))
);
const QuizzesPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizzesPage }))
);
const QuizPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizPage }))
);
const QuizResultsPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizResultPage }))
);
const QuizProgressByCoursePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizProgressByCoursePage }))
);
const QuizProgressByCourseSectionPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizProgressByCourseSectionPage }))
);
const TicketsPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.TicketsPage }))
);
const EventsPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.EventsPage }))
);
const EventPage = lazy(() =>
  import('@/pages/Common').then(module => ({ default: module.EventPage }))
);
const ChatPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.ChatPage }))
);

const ProfileConfigurationPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.ProfileConfigurationPage }))
);
const ExamGroupPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.ExamGroupPage }))
);

const RankingPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.RankingPage }))
);

const CourseSummaryPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.CourseSummaryPage }))
);

const CoursesListPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.CoursesListPage }))
);

const RateYourExperiencePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.RateYourExperiencePage }))
);
const TutorVotePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.TutorVotePage }))
);
const TeacherVotePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.TeacherVotePage }))
);
const LiveClassVotePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.LiveClassVotePage }))
);
const QuizConfigurationPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizConfigurationPage }))
);
const QuizHistoryPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.QuizHistoryPage }))
);
const FlashcardsCollectionPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.FlashcardsCollectionPage }))
);
const FlashcardsPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.FlashcardsPage }))
);
const FlashcardsPracticePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.FlashcardsPracticePage }))
);

const LiveClassPage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.LiveClassPage }))
);

const ExamScorePage = lazy(() =>
  import('@/pages/Student').then(module => ({ default: module.ExamScorePage }))
);

export const STUDENT: IRoute[] = [
  {
    title: 'ONBOARDING',
    path: '/onboarding',
    icon: 'Undefined',
    Component: OnboardingPage,
    showInMenu: false
  },
  {
    title: 'DASHBOARD',
    path: '/dashboard',
    icon: 'Home',
    Component: StudentDashboardPage,
    showInMenu: true
  },
  {
    title: 'MY_COURSES',
    path: '/subscription/courses',
    icon: 'Book',
    Component: CoursesListPage,
    availableForFeatures: [EFeatures.CONTENT],
    showInMenu: true
  },
  {
    title: 'PRACTICE_WITH_TESTS',
    path: '/tests',
    icon: 'TestTool',
    Component: QuizzesPage,
    availableForFeatures: [EFeatures.QUIZZES],
    showInMenu: true,
    subroutes: [
      {
        title: 'ALL_TEST',
        path: '',
        icon: 'Undefined',
        Component: QuizzesPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: ':_id',
        icon: 'Undefined',
        Component: QuizPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'results/:_id',
        icon: 'Undefined',
        Component: QuizResultsPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'history',
        icon: 'Undefined',
        Component: QuizHistoryPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'progress',
        icon: 'Undefined',
        Component: QuizProgressByCoursePage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'progress/:courseId',
        icon: 'Undefined',
        Component: QuizProgressByCourseSectionPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'configuration',
        icon: 'Undefined',
        Component: QuizConfigurationPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'flashcards',
        icon: 'Undefined',
        Component: FlashcardsCollectionPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'flashcards/collection/:collectionId',
        icon: 'Undefined',
        Component: FlashcardsPage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      },
      {
        title: 'ALL_TEST',
        path: 'flashcards/practice/:collectionId',
        icon: 'Undefined',
        Component: FlashcardsPracticePage,
        availableForFeatures: [EFeatures.QUIZZES],
        showInMenu: false
      }
    ]
  },
  {
    title: 'CALENDAR',
    path: '/content/calendar',
    icon: 'EventSchedule',
    Component: EventsPage,
    availableForFeatures: [EFeatures.LIVE_CLASSES, EFeatures.PLANIFICATION],
    showInMenu: true,
    TitleAddon: LiveEventLottie,
    subroutes: [
      {
        title: 'LIVE_EVENTS',
        path: ':_id',
        icon: 'Undefined',
        Component: EventPage,
        availableForFeatures: [EFeatures.LIVE_CLASSES],
        showInMenu: false
      }
    ]
  },
  {
    title: 'MY_QUESTIONS',
    path: 'tickets',
    icon: 'Undefined',
    Component: TicketsPage,
    showInMenu: true
  },
  {
    title: 'CHAT_TUTOR',
    path: 'chat',
    icon: 'Undefined',
    Component: ChatPage,
    availableForFeatures: [EFeatures.TUTORING],
    showInMenu: true,
    TitleAddon: PendingChatsBadge
  },
  {
    title: 'CHAT_TUTOR',
    path: 'chat/:tutorId/:studentId',
    icon: 'Undefined',
    Component: ChatPage,
    availableForFeatures: [EFeatures.TUTORING],
    showInMenu: false
  },
  {
    title: 'RANKING',
    path: 'ranking',
    icon: 'Undefined',
    Component: RankingPage,
    showInMenu: false
  },
  {
    title: 'EXAM_GROUPS',
    path: 'exam-groups',
    icon: 'Undefined',
    Component: ExamGroupPage,
    availableForFeatures: [EFeatures.ANNOUNCEMENTS],
    showInMenu: true
  },
  {
    title: 'RATE_YOUR_EXPERIENCE',
    path: 'rate-your-experience',
    icon: 'Undefined',
    Component: RateYourExperiencePage,
    showInMenu: false,
    subroutes: [
      {
        title: 'TUTOR_VOTE',
        path: 'tutor-vote',
        icon: 'Undefined',
        Component: TutorVotePage,
        showInMenu: false
      },
      {
        title: 'TEACHER_VOTE',
        path: 'teacher-vote',
        icon: 'Undefined',
        Component: TeacherVotePage,
        showInMenu: false
      },
      {
        title: 'LIVE_CLASS_VOTE',
        path: 'live-class-vote',
        icon: 'Undefined',
        Component: LiveClassVotePage,
        showInMenu: false
      }
    ]
  },
  {
    title: 'COURSES',
    path: '/subscription',
    icon: 'Undefined',
    Component: CoursePage,
    showInMenu: false,
    availableForFeatures: [EFeatures.CONTENT],
    subroutes: [
      {
        title: 'COURSES',
        path: 'courses',
        icon: 'Undefined',
        Component: CoursesListPage,
        availableForFeatures: [EFeatures.CONTENT],
        showInMenu: false
      },
      {
        title: 'COURSES',
        path: 'summary',
        icon: 'Undefined',
        Component: CourseSummaryPage,
        availableForFeatures: [EFeatures.CONTENT],
        showInMenu: false
      },
      {
        title: 'COURSES',
        path: ':_id',
        icon: 'Undefined',
        Component: CoursePage,
        availableForFeatures: [EFeatures.CONTENT],
        showInMenu: false
      }
    ]
  },
  {
    title: 'PROFILE',
    path: '/profile/configuration',
    icon: 'Undefined',
    Component: ProfileConfigurationPage,
    showInMenu: false,
    backgroundColor: 'white'
  },
  {
    title: 'LIVE_CLASS',
    path: '/live-class/:meetingId',
    icon: 'Undefined',
    Component: LiveClassPage,
    showInMenu: false
  },
  {
    title: 'EXAM_SCORES',
    path: 'exam-scores',
    icon: 'Undefined',
    Component: ExamScorePage,
    showInMenu: false
  }
];
